import { Box } from '@primer/react';
import { useReleaseFeatureGate } from '@upright/ui/common/upright/model/output/releases/hooks';
import { CSSProperties } from 'react';
import { Bounds, isNonEmpty, Side } from '../../../impact-lens-profile/common/layout/types';
import { useCarouselLayout } from '../../../impact-lens-profile/context';
import { useNetImpactLensContext } from '../common/use-net-impact-lens-context';
import { LegendArea } from './legend-area';
import { getVersionAndRelease } from '@upright/ui/untidy/redux/selectors';
import { ModelReleaseText } from '@upright/ui/common/upright/model/output/model-release-text';
import { useSelector } from 'react-redux';
import { TabletOrWiderOnly } from '@upright/ui/common/generic/layout/breakpoint-utils';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useUnifiedShell } from '@upright/ui/views/unified-shell/use-unified-shell';

export function AdditionalInfoArea() {
  const context = useNetImpactLensContext();
  const options = context?.options;
  const leftArea = useCarouselLayout().additionalInfoArea(Side.LEFT);
  const rightArea = useCarouselLayout().additionalInfoArea(Side.RIGHT);

  const { release: selectedRelease, version: selectedVersion } = useSelector(getVersionAndRelease);
  const release = context?.data?.release ?? selectedRelease;
  const version = context?.data?.version ?? selectedVersion;

  const allowValueChains = useReleaseFeatureGate('value-chain-splits');
  const showLegend = context?.state.showValueChains && allowValueChains;
  // The unified profiles keep the release under Help → Data sources and disclaimer (PLT-6866), so
  // the line under the chart only repeated it (PLT-7238, item 7). A static render (image export,
  // PDF) leaves the app behind and keeps it.
  const unifiedShell = useUnifiedShell();
  const showReleaseText = !unifiedShell.enabled || !!options?.staticMode;

  return (
    <>
      <TabletOrWiderOnly>
        {showReleaseText && isNonEmpty(leftArea) && (
          <Box style={getWrapperStyle(leftArea)}>
            <Box backgroundColor={BouColor.WHITE} marginLeft={3}>
              <ModelReleaseText
                release={release}
                version={version}
                language={options?.language}
                variantLinkProps={
                  context?.options.versionTextOptions.hideVariantLink
                    ? undefined
                    : {
                        type: context?.data?.entity?.type,
                        variantId: context?.data?.modelResultId?.toString(),
                        companyId: context?.data?.entity?.id,
                      }
                }
                showTimeZone={!!(options?.staticMode || options?.versionTextOptions.showTimezone)}
              />
            </Box>
          </Box>
        )}
        {isNonEmpty(rightArea) && (
          <Box
            style={getWrapperStyle(rightArea)}
            sx={{
              justifyContent: 'end',
            }}
          >
            {showLegend && <LegendArea />}
          </Box>
        )}
      </TabletOrWiderOnly>
    </>
  );
}

function getWrapperStyle(area: Bounds) {
  const common: CSSProperties = {
    top: area.y1,
    left: area.x1,
    width: area.width,
    height: area.height,
    maxHeight: area.height,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
  };
  return common;
}
